/**
 * Machine-readable codes carried on `createError({ data: { code } })`.
 *
 * The client branches on these, never on `message`. Message text is human-facing
 * copy — it gets rewritten, and it gets translated — so matching on it makes
 * control flow depend on prose, and it fails silently the day someone improves
 * the wording. `NO_ORGANIZATION` was already using this shape before this file
 * existed; the convention is just named in one place now.
 *
 * Only add a code when the client needs to *act* differently. A code the client
 * merely displays is a message, not a code.
 */
export const API_ERROR_CODES = {
  /** The caller has a user row but has not created or joined an organization. */
  noOrganization: 'NO_ORGANIZATION',
  /**
   * The `x-marcus-org-override` header named an organization the caller may not act as, or one that
   * no longer exists. The client ACTS on this: it drops the stored override rather than sending a
   * header that will refuse every subsequent request. When the override lived in a server-set
   * cookie the server cleared it; the client owns it now, so the server has to say so (MAR-427).
   */
  orgOverrideInvalid: 'ORG_OVERRIDE_INVALID',
  /**
   * The presented token belongs to an MFA-enrolled user but carries no
   * `firebase.sign_in_second_factor` — a session that predates enrollment, or one that never
   * completed a second factor, silently refreshing forever otherwise. The client ACTS on this: it
   * signs the caller out so their next sign-in goes through Firebase's own (native) TOTP challenge.
   * See `server/utils/firebase-admin.ts`.
   */
  mfaRequired: 'MFA_REQUIRED',
  /**
   * The caller's organization requires all members to enroll in TOTP 2FA (`organizations.require_mfa = true`),
   * and this user has not yet enrolled a verified factor (`MAR-1181`). The client ACTS on this: it redirects
   * immediately to `/app/settings/security` so the member can complete TOTP enrollment without a grace period.
   */
  mfaSetupRequired: 'MFA_SETUP_REQUIRED',
  /**
   * `POST /api/orgs/create` from a caller who already belongs to an organization — typically one who
   * joined in another tab while this one sat on the create form. The client ACTS on this: the setup
   * page says that, instead of the generic "could not create" (MAR-933, audit r3). The route's other
   * 400s (a blank or too-long name) carry no code.
   */
  alreadyInOrganization: 'ALREADY_IN_ORGANIZATION',
  /**
   * `POST /api/orgs/create` with a country signup is not open in (only El Salvador is, MAR-1041). The
   * client ACTS on this: the setup page shows its own Spanish sentence saying Marcus is not available
   * there yet, never the server's text. No organization is created.
   */
  signupCountryNotSupported: 'SIGNUP_COUNTRY_NOT_SUPPORTED',
  /**
   * `PUT /api/profile` with an onboarding `step` action for a step the user's seat plan does not
   * include (clients or cases without case management, MAR-978). Nothing is stored: a stored answer
   * would hide that step's reminder once the plan is raised. The client ACTS on it: the setup page
   * says it in its own words (`onboarding.failure.step_not_allowed`), never the server's text.
   */
  onboardingStepNotAllowed: 'ONBOARDING_STEP_NOT_ALLOWED',
  /**
   * The plan step's checkout refusals (MAR-1047), each answered by billing. The client ACTS on them:
   * the plan screen says each in its own Spanish words instead of showing billing's English.
   */
  invalidFiscalProfile: 'INVALID_FISCAL_PROFILE',
  voucherInvalid: 'VOUCHER_INVALID',
  voucherExpired: 'VOUCHER_EXPIRED',
  voucherUsed: 'VOUCHER_USED',
  notOrganizationAdmin: 'NOT_ORGANIZATION_ADMIN',
  planNotPurchasable: 'PLAN_NOT_PURCHASABLE',
  billingUnavailable: 'BILLING_UNAVAILABLE',
  /**
   * MAR-1474: billing has no payment provider for the organization's country, so it refused to open a paid
   * checkout (billing's `checkout_unavailable`) and nothing was opened or charged. The client ACTS on it: the plan
   * step says online payment is not available yet, not that billing is down.
   */
  checkoutUnavailable: 'CHECKOUT_UNAVAILABLE',
  /**
   * The caller lacks `billing:manage` (MAR-1269): they may administer the organization, or its
   * members, but not spend its money. Sent by `requirePermission(event, 'billing:manage')` on every
   * billing route that spends (checkout, quote, cancel, voucher, fiscal profile, a checkout's payment),
   * and — once S3 (MAR-1260) lets a seat assign charge — by billing's `not_allowed_to_spend`. The
   * client ACTS on it: the billing page and the seat screen say "you may not spend", not "only an
   * admin", which would be false for a custom role that administers members.
   */
  billingNotAllowedToSpend: 'BILLING_NOT_ALLOWED_TO_SPEND',
  /**
   * The simulated hosted checkout (MAR-1130). The client ACTS on both: the page says it is not
   * available (the simulator is off, or the checkout is not one it can pay) and offers the way back,
   * never a card form.
   */
  simulatedCheckoutOff: 'SIMULATED_CHECKOUT_OFF',
  checkoutNotFound: 'CHECKOUT_NOT_FOUND',
  /**
   * The seat screen's refusals (MAR-1048), each answered by billing. The client ACTS on them: the
   * screen says each in its own words (`app/utils/seatFailure.ts`) instead of billing's English.
   * `NOT_ORGANIZATION_ADMIN` and `BILLING_UNAVAILABLE` above are shared with the plan step.
   */
  seatPlanNotPurchased: 'SEAT_PLAN_NOT_PURCHASED',
  seatCannotUnassignLastOwner: 'SEAT_CANNOT_UNASSIGN_LAST_OWNER',
  seatNoSubscription: 'SEAT_NO_SUBSCRIPTION',
  seatUnknownPlan: 'SEAT_UNKNOWN_PLAN',
  /** Billing has no price for the plan (`invalid_order`), so no licence of it can be given. */
  seatPlanNotPriced: 'SEAT_PLAN_NOT_PRICED',
  seatNotMember: 'SEAT_NOT_MEMBER',
  /**
   * A seat change or a licensed invite that buys seats (MAR-1260, mapped by MAR-1264). The client ACTS
   * on each: the seat screen and the invite form say it in their own words, the member's plan stays
   * as it was, and nothing was charged. `QUOTE_REQUIRED` / `QUOTE_STALE`: the confirmed price is
   * missing or no longer true, so the admin picks again. `PAST_DUE`: only free seats can be reassigned.
   * `SUBSCRIPTION_CANCELING`: seats are frozen until the cycle ends. `NOT_ALLOWED`: the seats are a
   * superadmin's grant (MAR-1277), which an org admin cannot change.
   */
  seatQuoteRequired: 'SEAT_QUOTE_REQUIRED',
  seatQuoteStale: 'SEAT_QUOTE_STALE',
  seatPaymentDeclined: 'SEAT_PAYMENT_DECLINED',
  seatPaymentPending: 'SEAT_PAYMENT_PENDING',
  seatNoPaymentMethod: 'SEAT_NO_PAYMENT_METHOD',
  seatPastDue: 'SEAT_PAST_DUE',
  seatSubscriptionCanceling: 'SEAT_SUBSCRIPTION_CANCELING',
  seatNotAllowed: 'SEAT_NOT_ALLOWED',
  /** A voucher on an invite (MAR-1311): it is checked against a plan's price, so the invite must name a plan. */
  inviteVoucherNeedsPlan: 'INVITE_VOUCHER_NEEDS_PLAN',
  /**
   * The members page's licence refusal (MAR-1309): `LICENCE_NOT_FOUND`, no such licence in this organization.
   * Any licence, the last included, can be stopped (a stopped slot keeps the subscription alive).
   */
  licenceNotFound: 'LICENCE_NOT_FOUND',
  /** A plan's cap was lowered below the licences its members hold (MAR-1257): unassign or stop some first. */
  licenceCapBelowActive: 'LICENCE_CAP_BELOW_ACTIVE',
  /**
   * Assigning never buys (MAR-1398, MAR-1400): the organization has no FREE licence of that plan (billing's
   * `no_free_slot`). Nothing was changed or charged. The client ACTS on it: it offers to buy one (the plan's cap).
   */
  licenceNoFreeSlot: 'LICENCE_NO_FREE_SLOT',
  /**
   * The caller lacks `licences:assign` (MAR-1400): sent by `requirePermission(event, 'licences:assign')` and for
   * billing's `not_allowed_to_assign`. The client says "you may not assign licences", not "only an admin".
   */
  licenceNotAllowedToAssign: 'LICENCE_NOT_ALLOWED_TO_ASSIGN',
  /**
   * The organization has no plan (billing's subscription is not `active`/`past_due`), so it cannot invite
   * (MAR-1452, D19): a free trial organization buys a plan first. Sent by `POST /api/orgs/invites` and its
   * resend. The members page ACTS on it: it hides the invite controls and says why.
   */
  orgPlanRequiredToInvite: 'ORG_PLAN_REQUIRED_TO_INVITE',
} as const

export type ApiErrorCode = (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES]

/**
 * Read the code off a thrown `$fetch` error, or null when there isn't one.
 *
 * The nesting is worth writing down once: ofetch puts the parsed response body
 * on `FetchError.data`, and h3 puts `createError`'s `data` inside that body — so
 * the code lives at `err.data.data.code`, not `err.data.code`. Getting that
 * wrong yields `undefined` rather than an error, which is exactly the kind of
 * quiet miss this helper exists to prevent.
 */
export function apiErrorCode(err: unknown): ApiErrorCode | null {
  const code = (err as { data?: { data?: { code?: unknown } } })?.data?.data?.code
  return typeof code === 'string' ? (code as ApiErrorCode) : null
}
